/**
 * Affordance discovery — which DOM elements get a Learn-Mode badge, and with
 * which tip.
 *
 * Resolution order (identical for every app since the 2026-10-09 merge):
 *   1. Caller-supplied extra anchors (portal tour steps) claim their targets.
 *   2. Every unclaimed `[data-help-context]` element resolves its tip from the
 *      STATIC registry first, then from remote (server) tips. Static always
 *      wins: the help APIs derive their items from the same registries,
 *      flattened to one string, so a static hit is strictly richer.
 *
 * The hook re-scans on mount, when remote tips arrive, and (rAF-debounced) on
 * any DOM child-list mutation — so late-hydrating islands, tab switches and
 * client-side view changes pick up their badges without a page reload.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ExtraAffordance, LearnAffordance, LearnTip, RemoteTipLoader } from "./types";

const EMPTY_TIPS: ReadonlyMap<string, LearnTip> = new Map();

/** Scan the document for affordances. Pure apart from reading the DOM. */
export function scanAffordances(
  pageKey: string,
  resolveStaticTip: (contextKey: string) => LearnTip | null,
  remoteTips: ReadonlyMap<string, LearnTip>,
  collectExtraAffordances?: (pageKey: string) => ExtraAffordance[],
  root: ParentNode = document,
  excludeContextKeys?: readonly string[]
): LearnAffordance[] {
  const result: LearnAffordance[] = [];
  const claimed = new Set<Element>();
  const excluded = excludeContextKeys && excludeContextKeys.length > 0 ? new Set(excludeContextKeys) : null;

  for (const extra of collectExtraAffordances?.(pageKey) ?? []) {
    if (claimed.has(extra.target)) continue;
    claimed.add(extra.target);
    result.push({ key: extra.key, contextKey: null, target: extra.target, tip: extra.tip });
  }

  const seen = new Map<string, number>();
  for (const target of root.querySelectorAll<HTMLElement>("[data-help-context]")) {
    if (claimed.has(target)) continue;
    const contextKey = target.getAttribute("data-help-context");
    if (!contextKey) continue;
    if (excluded?.has(contextKey)) continue;
    const tip = resolveStaticTip(contextKey) ?? remoteTips.get(contextKey) ?? null;
    if (!tip) continue;
    claimed.add(target);
    // Repeated anchors (two elements sharing a context key) each get a badge
    // with a distinct identity: help:<key>, help:<key>#2, help:<key>#3 …
    const n = (seen.get(contextKey) ?? 0) + 1;
    seen.set(contextKey, n);
    result.push({ key: n === 1 ? `help:${contextKey}` : `help:${contextKey}#${n}`, contextKey, target, tip });
  }

  return result;
}

/** True when two scans produced the same badges (keys, targets AND tips). */
export function sameAffordances(a: readonly LearnAffordance[], b: readonly LearnAffordance[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((x, i) => {
    const y = b[i];
    return y !== undefined && x.key === y.key && x.target === y.target && x.tip === y.tip;
  });
}

export interface UseLearnAffordancesOptions {
  pageKey: string;
  resolveStaticTip: (contextKey: string) => LearnTip | null;
  loadRemoteTips?: RemoteTipLoader;
  collectExtraAffordances?: (pageKey: string) => ExtraAffordance[];
  /** Context keys that never get a badge (skipped during the scan). */
  excludeContextKeys?: readonly string[];
}

/** Live affordance list for the current page. */
export function useLearnAffordances(options: UseLearnAffordancesOptions): LearnAffordance[] {
  const { pageKey } = options;
  const [affordances, setAffordances] = useState<LearnAffordance[]>([]);
  const remoteRef = useRef<ReadonlyMap<string, LearnTip>>(EMPTY_TIPS);
  const rafRef = useRef<number | null>(null);
  // Latest callbacks, so apps may pass inline lambdas without re-subscribing.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const refresh = useCallback(() => {
    const { resolveStaticTip, collectExtraAffordances, excludeContextKeys } = optionsRef.current;
    const fresh = scanAffordances(
      pageKey,
      resolveStaticTip,
      remoteRef.current,
      collectExtraAffordances,
      document,
      excludeContextKeys
    );
    setAffordances((current) => (sameAffordances(current, fresh) ? current : fresh));
  }, [pageKey]);

  const scheduleRefresh = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      refresh();
    });
  }, [refresh]);

  // Remote tips (declared first so a page change resets them BEFORE the
  // re-scan below) — fail-soft: any rejection leaves the static layer in place.
  useEffect(() => {
    remoteRef.current = EMPTY_TIPS;
    const load = optionsRef.current.loadRemoteTips;
    if (!load) return;
    const controller = new AbortController();
    load(pageKey, controller.signal)
      .then((tips) => {
        if (controller.signal.aborted) return;
        remoteRef.current = tips;
        refresh();
      })
      .catch(() => {
        // Offline / 404 / malformed — static tips still render.
      });
    return () => controller.abort();
  }, [pageKey, refresh]);

  // Initial scan (and re-scan on page change).
  useEffect(() => {
    refresh();
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [refresh]);

  // A changed exclude list re-scans (skipped on mount — the initial scan above
  // already used it). `refresh` is intentionally NOT re-run on every render.
  const excludeSig = options.excludeContextKeys?.join("\u0000") ?? "";
  const lastExcludeSig = useRef(excludeSig);
  useEffect(() => {
    if (lastExcludeSig.current === excludeSig) return;
    lastExcludeSig.current = excludeSig;
    refresh();
  }, [excludeSig, refresh]);

  // Late-hydrating islands, tab switches, view changes.
  useEffect(() => {
    const observer = new MutationObserver(() => scheduleRefresh());
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [scheduleRefresh]);

  return affordances;
}
